import { useState, useEffect, useRef } from "react";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdBusiness,
  MdPeople,
  MdEmail,
  MdPhone,
  MdLanguage,
  MdLocationOn,
  MdArrowBack,
  MdCameraAlt,
} from "react-icons/md";
import ActionMenu from "../components/common/ActionMenu";
import api from "../services/api";
import toast from "react-hot-toast";
import "./Organizations.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : "http://localhost:5000";
const COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];
const initials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const avatarColor = (name) =>
  COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];

const UserAvatar = ({ user, size = 36 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: src ? "transparent" : avatarColor(user?.name),
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={user.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = initials(user?.name);
          }}
        />
      ) : (
        initials(user?.name)
      )}
    </div>
  );
};

const OrgLogo = ({ org, size = 72, className = "" }) => {
  const [imgError, setImgError] = useState(false);
  const src =
    org?.logo && !imgError
      ? org.logo.startsWith("http")
        ? org.logo
        : `${API_URL}${org.logo}`
      : null;
  const color = avatarColor(org?.name);
  return (
    <div
      className={`org-logo-wrap ${className}`.trim()}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.2,
        background: src ? "transparent" : color + "22",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
        border: `2px solid ${color}33`,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={org.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setImgError(true)}
        />
      ) : (
        <MdBusiness color={color} size={size * 0.5} />
      )}
    </div>
  );
};

// ─── Org Modal ────────────────────────────────────────────────
const OrgModal = ({ org, onClose, onSaved }) => {
  const [form, setForm] = useState(
    org
      ? {
          name: org.name,
          description: org.description,
          address: org.address,
          phone: org.phone,
          email: org.email,
          website: org.website,
          isActive: org.isActive,
        }
      : {
          name: "",
          description: "",
          address: "",
          phone: "",
          email: "",
          website: "",
          isActive: true,
        },
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (org) {
        await api.put(`/organizations/${org._id}`, form);
        toast.success("Organization updated");
      } else {
        await api.post("/organizations", form);
        toast.success("Organization created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{org ? "Edit Organization" : "New Organization"}</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Organization Name *</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-control"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  className="form-control"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                className="form-control"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input
                className="form-control"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={String(form.isActive)}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.value === "true" })
                }
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : org ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Org Detail ───────────────────────────────────────────────
const OrgDetail = ({ org: initialOrg, onBack }) => {
  const [org, setOrg] = useState(initialOrg);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api
      .get(`/organizations/${org._id}/staff`)
      .then(({ data }) => setStaff(data.data))
      .finally(() => setLoading(false));
  }, [org._id]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post(`/organizations/${org._id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrg(data.data);
      toast.success("Logo updated!");
    } catch (err) {
      toast.error("Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="org-detail-page">
      <div className="org-detail-header">
        <button
          type="button"
          className="org-detail-back-btn"
          onClick={onBack}
          aria-label="Back"
        >
          <MdArrowBack size={24} />
        </button>
        <div
          className="org-detail-logo-wrap"
          style={{ width: 100, height: 100 }}
        >
          <OrgLogo org={org} size={100} />
          <button
            type="button"
            className="org-detail-logo-camera"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Change logo"
          >
            {uploading ? (
              <div
                className="spinner"
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
            ) : (
              <MdCameraAlt size={18} />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleLogoUpload}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="org-detail-title">{org.name}</h1>
          <p className="org-detail-subtitle">
            {org.description || "Click camera to upload logo"}
          </p>
          <span
            className={`badge ${org.isActive ? "badge-done" : "badge-review"}`}
            style={{ marginTop: 10, fontSize: "0.75rem" }}
          >
            {org.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="org-detail-grid">
        <div className="org-detail-card">
          <h3>Details</h3>
          {[
            { icon: <MdEmail size={18} />, label: "Email", val: org.email },
            { icon: <MdPhone size={18} />, label: "Phone", val: org.phone },
            {
              icon: <MdLocationOn size={18} />,
              label: "Address",
              val: org.address,
            },
            {
              icon: <MdLanguage size={18} />,
              label: "Website",
              val: org.website,
              link: true,
            },
          ].map(
            (item) =>
              item.val && (
                <div key={item.label} className="org-detail-detail-row">
                  <span className="icon-wrap">{item.icon}</span>
                  <div>
                    <div className="org-detail-detail-label">{item.label}</div>
                    {item.link ? (
                      <a
                        href={item.val}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="org-detail-detail-value"
                      >
                        {item.val}
                      </a>
                    ) : (
                      <div className="org-detail-detail-value">{item.val}</div>
                    )}
                  </div>
                </div>
              ),
          )}
        </div>
        <div className="org-detail-card">
          <h3>Staff</h3>
          <div className="org-detail-staff-hero">
            <div>
              <div className="org-detail-staff-number">{staff.length}</div>
              <div className="org-detail-staff-label">members</div>
            </div>
            <div className="org-detail-staff-avatars">
              {staff.slice(0, 8).map((u) => (
                <div
                  key={u._id}
                  className="org-detail-staff-avatar"
                  style={{
                    background: u.avatar ? "transparent" : avatarColor(u.name),
                  }}
                  title={u.name}
                >
                  {u.avatar ? (
                    <img
                      src={
                        u.avatar.startsWith("http")
                          ? u.avatar
                          : `${API_URL}${u.avatar}`
                      }
                      alt=""
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentNode.textContent = initials(u.name);
                      }}
                    />
                  ) : (
                    initials(u.name)
                  )}
                </div>
              ))}
              {staff.length > 8 && (
                <div className="org-detail-staff-more">+{staff.length - 8}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="org-detail-table-card">
        <div className="table-header">Staff Members ({staff.length})</div>
        {loading ? (
          <div className="loading" style={{ minHeight: 200 }}>
            <div className="spinner" />
          </div>
        ) : staff.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <MdPeople size={48} />
            <h3>No staff yet</h3>
            <p>Assign users to this organization.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Designation</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((u) => (
                  <tr key={u._id}>
                    <td>
                      <div className="org-detail-member-cell">
                        <UserAvatar user={u} size={44} />
                        <div>
                          <div className="org-detail-member-name">{u.name}</div>
                          <div className="org-detail-member-email">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted">{u.designation || "—"}</td>
                    <td className="text-muted">{u.phone || "—"}</td>
                    <td>
                      <span
                        className={`badge ${u.role === "admin" ? "badge-critical" : "badge-todo"}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${u.isActive ? "badge-done" : "badge-review"}`}
                      >
                        {u.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const Organizations = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [viewOrg, setViewOrg] = useState(null);

  const fetchOrgs = async () => {
    const { data } = await api.get("/organizations");
    setOrgs(data.data);
    setLoading(false);
  };
  useEffect(() => {
    fetchOrgs();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this organization? Staff will be unlinked."))
      return;
    await api.delete(`/organizations/${id}`);
    toast.success("Organization deleted");
    fetchOrgs();
  };

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );

  if (viewOrg)
    return (
      <main className="page org-page">
        <OrgDetail
          org={viewOrg}
          onBack={() => {
            setViewOrg(null);
            fetchOrgs();
          }}
        />
      </main>
    );

  return (
    <main className="page org-page">
      {(showModal || editOrg) && (
        <OrgModal
          org={editOrg}
          onClose={() => {
            setShowModal(false);
            setEditOrg(null);
          }}
          onSaved={fetchOrgs}
        />
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">Organizations</h1>
          <p className="page-subtitle">
            {orgs.length} organization{orgs.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <MdAdd /> New Organization
        </button>
      </div>

      {orgs.length === 0 ? (
        <div className="empty-state">
          <MdBusiness size={64} />
          <h3>No organizations yet</h3>
          <p>Create your first organization and assign staff to it.</p>
          <button
            className="btn btn-primary"
            style={{ marginTop: 20 }}
            onClick={() => setShowModal(true)}
          >
            <MdAdd /> Create Organization
          </button>
        </div>
      ) : (
        <div className="org-grid">
          {orgs.map((org) => (
            <div
              key={org._id}
              className="org-card"
              onClick={() => setViewOrg(org)}
            >
              <div
                className="org-card-accent"
                style={{ background: avatarColor(org.name) }}
              />
              <div className="org-card-body">
                <div className="org-card-top">
                  <div className="org-card-brand">
                    <OrgLogo org={org} size={72} />
                    <div style={{ minWidth: 0 }}>
                      <div className="org-card-name">{org.name}</div>
                      <span
                        className={`badge org-card-badge ${org.isActive ? "badge-done" : "badge-review"}`}
                      >
                        {org.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                      items={[
                        {
                          label: "Edit",
                          icon: <MdEdit size={15} />,
                          onClick: () => setEditOrg(org),
                        },
                        "divider",
                        {
                          label: "Delete",
                          icon: <MdDelete size={15} />,
                          onClick: () => handleDelete(org._id),
                          danger: true,
                        },
                      ]}
                    />
                  </div>
                </div>

                {org.description && (
                  <p className="org-card-desc">{org.description}</p>
                )}

                <div className="org-card-meta">
                  {org.email && (
                    <span className="org-card-meta-item">
                      <MdEmail size={16} />
                      {org.email}
                    </span>
                  )}
                  {org.phone && (
                    <span className="org-card-meta-item">
                      <MdPhone size={16} />
                      {org.phone}
                    </span>
                  )}
                </div>

                <div className="org-card-footer">
                  <div className="org-staff-block">
                    <div className="org-staff-avatars">
                      {(org.staffPreview || []).slice(0, 5).map((u) => (
                        <div
                          key={u._id}
                          className="org-staff-avatar"
                          style={{
                            background: u.avatar
                              ? "transparent"
                              : avatarColor(u.name),
                          }}
                          title={u.name}
                        >
                          {u.avatar ? (
                            <img
                              src={
                                u.avatar.startsWith("http")
                                  ? u.avatar
                                  : `${API_URL}${u.avatar}`
                              }
                              alt=""
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.parentNode.textContent = initials(
                                  u.name,
                                );
                              }}
                            />
                          ) : (
                            initials(u.name)
                          )}
                        </div>
                      ))}
                      {(!org.staffPreview || org.staffPreview.length === 0) && (
                        <div
                          className="org-staff-avatar"
                          style={{ background: "var(--surface3)" }}
                        >
                          <MdPeople size={18} color="var(--text-muted)" />
                        </div>
                      )}
                    </div>
                    <span>
                      <span className="org-staff-count">
                        {org.staffCount ?? 0}
                      </span>
                      <span className="org-staff-label">staff</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    className="org-card-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewOrg(org);
                    }}
                  >
                    View Staff →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default Organizations;
